import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { animated, easings, useTransition } from '@react-spring/web';
import classNames from 'classnames';
import { useWindowSize } from '../utils/hooks/use-window-size';
import { Helpers } from '../helpers/helpers';
import CloseIcon from '../icons/Close';
import ArrowBackIcon from '../icons/ArrowBack';
import { useScrollLock } from '../utils/hooks/use-scroll-lock';

export const MobileTray = ({
  isVisible,
  setIsVisible,
  title,
  onBackClick,
  children,
  useHeaderBorder = true,
  className
}) => {
  const [currentTouchMove, setCurrentTouchMove] = useState();
  const [trayHeight, setTrayHeight] = useState('');

  const prevTouchMove = useRef();
  const clickOutRef = useRef();
  const isMobile = useRef(Helpers.isMobile());

  const { setScrollLocked } = useScrollLock();

  useEffect(() => {
    setScrollLocked(isVisible);
  }, [isVisible]);

  const windowDims = useWindowSize(false);
  const transitions = useTransition(isVisible, {
    from: { y: '100%' },
    enter: { y: '0%' },
    leave: { y: '100%' },
    config: {
      duration: 400,
      easing: easings.easeOutCirc
    }
  });

  useEffect(() => {
    const trayHeightValue = isMobile.current && windowDims?.height
      ? `${windowDims.height - 32}px`
      : 'calc(100vh - 32px)';
    setTrayHeight(trayHeightValue);
  }, [windowDims?.height]);

  useEffect(() => {
    if (!isVisible || !prevTouchMove.current || !currentTouchMove) {
      prevTouchMove.current = currentTouchMove;
      return;
    }
    const swipedDown = prevTouchMove.current < currentTouchMove;
    if (swipedDown) {
      setIsVisible(false);
    } else {
      prevTouchMove.current = currentTouchMove;
    }
  }, [currentTouchMove]);

  const handleClickOutClick = (e) => {
    if (e.target === clickOutRef.current) {
      setIsVisible(false);
    }
  };

  const handleScrollIndicatorTouchMove = (e) => {
    const touch = e.touches && e.touches[0];
    if (touch) {
      setCurrentTouchMove(touch.clientY);
    }
  };

  const handleScrollIndicatorTouchEnd = () => {
    setCurrentTouchMove(0);
  };

  const handleClose = (e) => {
    e.preventDefault();
    setIsVisible(false);
  };

  return transitions(
    (styles, isVisible) => isVisible && (
      <div
        className='mobile-tray-container fixed flex bottom'
        ref={clickOutRef}
        onClick={handleClickOutClick}
      >
        <animated.div
          className={classNames('tray full-width relative', className)}
          style={{
            ...styles,
            height: trayHeight
          }}
        >
          <div
            className='pull-indicator-container flex center middle'
            onTouchMove={handleScrollIndicatorTouchMove}
            onTouchEnd={handleScrollIndicatorTouchEnd}
          >
            <span className='pull-indicator' />
          </div>

          <div
            className={classNames('ps-mobile-tray-header', {
              'use-header-border': useHeaderBorder
            })}
          >
            {onBackClick && (
              <button
                className='flex middle btn-clear'
                style={{ marginRight: '8px' }}
                onClick={onBackClick}
              >
                <ArrowBackIcon width={24} height={24} />
              </button>
            )}

            <span className='ps-mobile-tray-title h4'>{title}</span>

            <button
              className='flex middle btn-clear'
              style={{ marginLeft: 'auto' }}
              onClick={handleClose}
            >
              <CloseIcon width={24} height={24} />
            </button>
          </div>

          <div>{children}</div>
        </animated.div>
      </div>
    )
  );
};

MobileTray.propTypes = {
  isVisible: PropTypes.bool,
  setIsVisible: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  title: PropTypes.string,
  onBackClick: PropTypes.func
};
